import { authenticationStore } from "@/state/auth/AuthStore";
import { setCurrentTransactionDetail } from "@/state/transactions/CurrentTransactionEvents";
import { requestPostMembersForOffice } from "@/state/users/UserEffects";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { Member } from "@/type/core/offices/offices.types";
import { toastError } from "@/utils/helpers/toast-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import ResetSignaturePositioningScreen from "@views/core/extranet/transactions/positioning/ResetSignaturePositioningScreen";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const PreLoadingResetSignaturePositioningScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const params = useParams<{ transactionId: string; reset: string }>();
  const { transactionId, reset } = params;

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const me = authStore?.user;
  const officesForCgu = me?.officesForCgu;
  const officeIds = useMemo(
    () => officesForCgu?.map((office) => office.id),
    [officesForCgu],
  );

  const [isPostMembersForOfficeLoading, setIsPostMembersForOfficeLoading] =
    useState<boolean>(true);
  const [
    isRetrieveTransactionDetailLoading,
    setIsRetrieveTransactionDetailLoading,
  ] = useState<boolean>(true);
  const [members, setMembers] = useState<Member[]>([]);

  const isLoading =
    isPostMembersForOfficeLoading || isRetrieveTransactionDetailLoading;

  useEffect(() => {
    if (officeIds) {
      setIsPostMembersForOfficeLoading(true);
      void requestPostMembersForOffice({ dto: officeIds });
    }
  }, [officeIds]);

  useEffect(() => {
    if (transactionId) {
      setIsRetrieveTransactionDetailLoading(true);
      TransactionHelper.retrieveTransactionDetailAndSetItIntoStore(
        transactionId,
        (transaction, transactionSignatures) => {
          setCurrentTransactionDetail(transaction);
          if (
            transaction.officeId &&
            transaction.signatories &&
            transaction.signatories.length
          ) {
            TransactionHelper.retrieveTransactionSignatoriesPicturesAndCertificate(
              transaction.signatories,
              transactionSignatures,
              transaction.officeId,
              () => setIsRetrieveTransactionDetailLoading(false),
            );
          } else {
            setIsRetrieveTransactionDetailLoading(false);
          }
        },
        !!reset,
      );
    }
  }, [transactionId]);

  useEffect(() => {
    return requestPostMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts);
      } else {
        toastError(t("error.toast.officesSearch"));
      }
      setIsPostMembersForOfficeLoading(false);
    });
  });

  return (
    !isLoading && (
      <ResetSignaturePositioningScreen
        members={members}
        officesForCgu={officesForCgu}
      />
    )
  );
};

export default PreLoadingResetSignaturePositioningScreen;
